import type { IFactura } from "../Facturas";
import type { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import { EstadoFacturacion, getEstadoFacturacion } from "./EstadoFacturacionIcon";
import { getFechaExpedicion } from "../../../utils/Factura";

// Fila del listado para la rectificativa negativa que se acaba de crear al anular una factura.
// `huerfana`: la factura anulada ya no está enlazada a su servicio (cambio de entidad), así que se abre con el
// detalle genérico y no muestra modalidad.
const filaAnulacion = (anulacion: IFacturaCompleta, huerfana: boolean): IFactura => ({
  _id: anulacion._id,
  numero: anulacion.numero,
  fechaFactura: anulacion.fechaFactura?.toString() ?? null,
  fechaExpedicion: getFechaExpedicion(anulacion),
  tipo: anulacion.tipo,
  modalidad: huerfana ? "" : "rectificativa",
  servicio: anulacion.servicio,
  pacienteId: anulacion.pacienteId ?? null,
  paciente: anulacion.receptor?.nombre ?? null,
  servicioId: "",
  total: anulacion.importeTotal,
  estado: getEstadoFacturacion(anulacion.estado, anulacion.estadoPago, anulacion.tipoRectificacion),
  facturaRelacionada: undefined,
  fechaPago: anulacion.metodoPago?.fechaPago?.toString() ?? null,
  moneda: anulacion.moneda,
  entityId: anulacion.entityId ?? null,
  huerfana,
});

// Tras anular una factura emitida: la original pasa a "Anulada" y aparece su rectificativa negativa.
export const aplicarAnulacion = (
  filas: IFactura[],
  facturaAnuladaId: string,
  anulacion?: IFacturaCompleta | null
): IFactura[] => {
  const actualizadas = filas.map(r => r._id === facturaAnuladaId ? { ...r, estado: EstadoFacturacion.ANULADA } : r);
  return anulacion && !filas.some(r => r._id === anulacion._id)
    ? [filaAnulacion(anulacion, false), ...actualizadas]
    : actualizadas;
};

// Tras cambiar la entidad de una factura:
//  - Borrador: es la misma factura (mismo id) con otra entidad; solo cambia su fila.
//  - Emitida (hay `anulacion`): aparece la nueva (Borrador) y la vieja pasa a "Anulada" junto a su negativa; en
//    cita y clase la vieja y la negativa quedan huérfanas (el servicio ya apunta a la factura nueva).
// Los listados se filtran por entidad: la fila que cambia de entidad dejará de verse en el filtro actual y
// aparecerá al cambiar el filtro, sin recargar.
export const aplicarCambioEntidad = (
  filas: IFactura[],
  facturaAnuladaId: string,
  facturaNueva: IFacturaCompleta,
  anulacion?: IFacturaCompleta | null
): IFactura[] => {
  if (facturaNueva._id === facturaAnuladaId) {
    return filas.map(r => r._id === facturaAnuladaId ? { ...r, entityId: facturaNueva.entityId ?? null } : r);
  }

  const anterior = filas.find(r => r._id === facturaAnuladaId);
  const nuevaReducida: IFactura = {
    ...(anterior as IFactura),
    _id: facturaNueva._id,
    numero: facturaNueva.numero,
    fechaFactura: facturaNueva.fechaFactura?.toString(),
    fechaExpedicion: getFechaExpedicion(facturaNueva),
    total: facturaNueva.importeTotal,
    estado: getEstadoFacturacion(facturaNueva.estado, facturaNueva.estadoPago),
    entityId: facturaNueva.entityId ?? null,
    huerfana: false,
  };
  const huerfana = !!anulacion && (anterior?.tipo === "cita" || anterior?.tipo === "clase");
  return [
    nuevaReducida,
    ...(anulacion ? [filaAnulacion(anulacion, huerfana)] : []),
    ...filas.map(r => r._id === facturaAnuladaId
      ? { ...r, estado: EstadoFacturacion.ANULADA, ...(huerfana ? { huerfana: true, modalidad: "" } : {}) }
      : r),
  ];
};
